import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, Clock, Trash2, Loader2, RefreshCw, Info, AlertTriangle, CheckCircle2, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import useChatsOwner from '../hooks/useChatsOwner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { r16pUiEnabled } from '../lib/r14Switch';
import { slotsApi } from '../lib/api';
import { chatsApi } from '../lib/chatsApi';
import { chatsKeys } from '../lib/chatsKeys';
import { slotKey } from '../lib/slotSelection';
import { classifyChatsError, chatsErrorText, chatLog } from '../lib/chatsView';
import {
  normaliseChatSettings, retentionLabel, isShortening, erasePhrase, eraseConfirmed, slotNameFrom, eraseResultText, REIMPORT_TEXT,
  DEFAULT_RETENTION_DAYS,
} from '../lib/chatDataView';

/*
 * r16pm (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.6 / 6.10 / 8.5 / 8.7, owner decision O8: /settings/chats
 * "Chat data" - the two customer screens the r16p Inbox round left out (its README: "NOT BUILT ... the merge round must decide").
 *
 *   Retention   how long message and reply text is kept: the server's choices (7 / 30 / 90 days, O8 default 30), GET / PUT
 *               /uiapi/chats/settings. A shorter period asks first (older text is erased at the next clean-up and cannot come
 *               back). The account's send caps, AI cap and "replies switched off" are shown read-only (an admin sets them).
 *   Erase       one slot's chat data, DELETE /uiapi/chats/slots/{slot}/data, behind a TYPED confirmation ("erase slot801"). The page
 *               says what goes (conversations, messages, replies - waiting ones are never sent -, AI suggestions; the slot key is
 *               destroyed first, so it cannot be undone; the slot's "DM Inbox (Chats)" settings go back to their defaults) and says
 *               plainly that ticking 771 again re-imports.
 * Both answer 404 chats_disabled while the server's chats switch is off. Logs: chatLog only (days, a slot<number> name, counts).
 * Switch: lib/r14Switch.js r16pUiEnabled - ON by default; OFF = the route goes to the dashboard and no entry point links here.
 */

const CARD = 'aurora-card space-y-4 p-5';

function Fact({ label, value, testid }) {
  return (
    <div className="min-w-0 rounded-xl bg-secondary/60 p-3" data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tnum text-foreground">{value}</p>
    </div>
  );
}

const fmtN = (v) => (Number.isFinite(v) ? v.toLocaleString('en-US') : 'not reported');

function RetentionCard({ settingsQ, owner }) {
  const queryClient = useQueryClient();
  const s = useMemo(() => (settingsQ.data ? normaliseChatSettings(settingsQ.data) : null), [settingsQ.data]);
  const [pick, setPick] = useState(null);
  const [ask, setAsk] = useState(false);
  useEffect(() => { if (s && pick === null) setPick(s.retentionDays); }, [s, pick]);
  const save = useMutation({
    mutationFn: (days) => chatsApi.saveSettings(days).then((r) => r.data),
    onSuccess: (data, days) => {
      queryClient.setQueryData(chatsKeys.settings(owner), data);
      queryClient.invalidateQueries({ queryKey: chatsKeys.info(owner) });
      chatLog('info', 'chat retention saved', { days });
      toast.success(`Message text is now kept ${days} days.`);
    },
    onError: (e, days) => {
      const c = classifyChatsError(e);
      chatLog('warn', 'chat retention refused', { days, status: c.status, code: c.code });
      toast.error(chatsErrorText(e, 'The retention was not saved'));
    },
  });
  if (settingsQ.isLoading) return <div className={cn(CARD, 'h-56 animate-pulse')} aria-busy="true" aria-label="Loading the chat settings" />;
  if (settingsQ.error) {
    return (
      <section className={CARD} role="alert" data-testid="chatdata-settings-error">
        <p className="text-sm text-muted-foreground">{chatsErrorText(settingsQ.error, 'The chat settings could not be loaded')}</p>
        <Button variant="outline" size="sm" className="tap rounded-full" onClick={() => settingsQ.refetch()}><RefreshCw className="h-4 w-4" />Try again</Button>
      </section>
    );
  }
  if (!s) return null;
  const current = s.retentionDays;
  const changed = pick !== null && pick !== current;
  const submit = () => {
    if (!changed || save.isPending) return;
    if (isShortening(current, pick)) { setAsk(true); return; }
    save.mutate(pick);
  };
  return (
    <section className={CARD} data-testid="chatdata-retention" aria-labelledby="chatdata-retention-title">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-primary/20"><Clock className="h-[18px] w-[18px]" /></span>
        <div className="min-w-0">
          <h2 id="chatdata-retention-title" className="font-heading text-base font-semibold">How long message text is kept</h2>
          <p className="mt-0.5 text-sm text-muted-foreground text-pretty">
            For every slot of this account. Text older than this is erased from NecroMancer&apos;s server; the conversation, the times and
            whether a message was in or out stay. Nothing changes on Instagram or on the phone.
          </p>
        </div>
      </div>
      <div role="radiogroup" aria-label="Keep message text for" className="grid grid-cols-1 gap-2 xs:grid-cols-3" data-testid="chatdata-retention-choices">
        {s.retentionChoices.map((d) => {
          const on = pick === d;
          return (
            <button key={d} type="button" role="radio" aria-checked={on} disabled={!s.retentionEditable || save.isPending}
              onClick={() => setPick(d)} data-testid={`chatdata-retention-${d}`}
              className={cn('tap rounded-xl px-3 py-2.5 text-left text-sm ring-1 transition-colors disabled:opacity-60',
                on ? 'bg-primary/15 text-primary ring-primary/40' : 'bg-secondary/40 text-foreground/90 ring-border hover:bg-secondary/70')}>
              <span className="block font-semibold tnum">{d} days</span>
              <span className="block text-xs text-muted-foreground">
                {d === DEFAULT_RETENTION_DAYS ? 'Recommended' : d < DEFAULT_RETENTION_DAYS ? 'Less kept' : 'More history'}
                {d === current ? ' · current' : ''}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="chatdata-retention-current">
        Now: {Number.isFinite(current) ? retentionLabel(current, current) : 'not reported by the server'}.
      </p>
      {changed && isShortening(current, pick) && (
        <p className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm" role="status" data-testid="chatdata-retention-shorter">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
          <span>Text older than {pick} days is erased at the next clean-up and cannot be brought back.</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button className="tap rounded-full" disabled={!changed || save.isPending || !s.retentionEditable} onClick={submit} data-testid="chatdata-retention-save">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Save
        </Button>
        {changed && <Button variant="ghost" className="tap rounded-full" onClick={() => setPick(current)} disabled={save.isPending}>Undo</Button>}
      </div>
      <div className="grid grid-cols-1 gap-2 xs:grid-cols-2 lg:grid-cols-4" data-testid="chatdata-caps">
        <Fact label="Replies a day" value={fmtN(s.dailySendCap)} testid="chatdata-cap-daily" />
        <Fact label="Replies an hour" value={fmtN(s.hourlySendCap)} testid="chatdata-cap-hourly" />
        <Fact label="AI suggestions a day" value={fmtN(s.aiDailyCap)} testid="chatdata-cap-ai" />
        <Fact label="Replies" value={s.repliesDisabled ? 'Switched off' : 'Allowed'} testid="chatdata-replies" />
      </div>
      <p className="text-xs text-muted-foreground">These limits are set for the account by NecroMancer; ask support to change them.</p>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="chatdata-retention-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Keep message text only {pick} days?</AlertDialogTitle>
            <AlertDialogDescription>
              Message and reply text older than {pick} days, in every slot of this account, is erased from NecroMancer&apos;s server at the
              next clean-up. It cannot be brought back, even if you choose a longer period later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chatdata-retention-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-warning text-background hover:bg-warning/90" data-testid="chatdata-retention-ok"
              onClick={() => { setAsk(false); save.mutate(pick); }}>Keep {pick} days</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function EraseCard({ slotNames, initialSlot, owner }) {
  const queryClient = useQueryClient();
  const [slot, setSlot] = useState(initialSlot || '');
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(null);
  const known = slotNames.includes(slot);
  const ok = known && eraseConfirmed(typed, slot);
  const erase = useMutation({
    mutationFn: (name) => chatsApi.eraseSlotData(name).then((r) => r.data),
    onSuccess: (data, name) => {
      setDone({ slot: name, text: eraseResultText(data, name) });
      setTyped('');
      // every chat answer of this account may hold that slot's rows: drop them all (the 'chats' root, this account only)
      queryClient.removeQueries({ queryKey: chatsKeys.threads(owner) });
      queryClient.invalidateQueries({ queryKey: chatsKeys.all(owner) });
      chatLog('info', 'slot chat data erased', { slot: name, rows: data && Number.isFinite(Number(data.rows)) ? Number(data.rows) : null, reset: !!(data && data.settingsReset) });
      toast.success(`${name}'s chat data is erased.`);
    },
    onError: (e, name) => {
      const c = classifyChatsError(e);
      chatLog('warn', 'slot chat data erase refused', { slot: name, status: c.status, code: c.code });
      toast.error(chatsErrorText(e, 'The chat data was not erased'));
    },
  });
  const phrase = erasePhrase(slot || 'slotN');
  return (
    <section className={cn(CARD, 'border-danger/25')} data-testid="chatdata-erase" aria-labelledby="chatdata-erase-title">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-danger/10 text-danger ring-1 ring-danger/25"><Trash2 className="h-[18px] w-[18px]" /></span>
        <div className="min-w-0">
          <h2 id="chatdata-erase-title" className="font-heading text-base font-semibold">Erase a slot&apos;s chat data</h2>
          <p className="mt-0.5 text-sm text-muted-foreground text-pretty">Removes everything NecroMancer stored from one slot&apos;s DM inbox. Nothing changes on Instagram or on the phone.</p>
        </div>
      </div>
      <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground text-pretty" data-testid="chatdata-erase-what">
        <li>Every conversation, message, reply and AI suggestion stored for the slot is deleted. Replies still waiting are ended and never sent.</li>
        <li>The slot&apos;s encryption key is destroyed first, so this cannot be undone.</li>
        <li>The slot&apos;s &quot;DM Inbox (Chats)&quot; settings go back to their defaults: &quot;Read my DM inbox&quot; (771) is unticked.</li>
        <li>API keys limited to chosen slots no longer cover this slot.</li>
      </ul>
      <p className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-foreground" role="note" data-testid="chatdata-reimport">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden /><span>{REIMPORT_TEXT}</span>
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 min-w-0">
          <Label htmlFor="chatdata-slot">Slot</Label>
          <Input id="chatdata-slot" list="chatdata-slot-names" value={slot} autoComplete="off" spellCheck={false}
            onChange={(e) => { setSlot(e.target.value.trim()); setTyped(''); setDone(null); }}
            placeholder="slot801" className="bg-secondary/60 border-border font-mono" data-testid="chatdata-slot" />
          <datalist id="chatdata-slot-names">{slotNames.slice(0, 2000).map((n) => <option key={n} value={n} />)}</datalist>
          {slot && !known && <p className="text-xs text-warning" data-testid="chatdata-slot-unknown">No slot of this account is called {slot}.</p>}
        </div>
        <div className="space-y-1.5 min-w-0">
          <Label htmlFor="chatdata-typed">Type <code className="font-mono text-danger">{phrase}</code> to confirm</Label>
          <Input id="chatdata-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} disabled={!known || erase.isPending}
            placeholder={phrase} className="bg-secondary/60 border-border font-mono" data-testid="chatdata-typed" />
        </div>
      </div>
      <Button className="tap rounded-full bg-danger text-white hover:bg-danger/90" disabled={!ok || erase.isPending}
        onClick={() => { if (ok && !erase.isPending) erase.mutate(slot); }} data-testid="chatdata-erase-btn">
        {erase.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        {known ? `Erase ${slot}'s chat data` : 'Erase the chat data'}
      </Button>
      {done && (
        <div className="space-y-1.5 rounded-xl border border-success/30 bg-success/10 p-3 text-sm" role="status" data-testid="chatdata-erase-done">
          <p className="text-success">{done.text}</p>
          <p className="text-foreground/90">{REIMPORT_TEXT}</p>
          <Link className="inline-flex items-center gap-1 font-medium text-primary hover:underline" to={`/slots/${encodeURIComponent(done.slot)}/settings`}>Open {done.slot}&apos;s settings</Link>
        </div>
      )}
    </section>
  );
}

export default function ChatDataPage() {
  usePageTitle('Chat data');
  const on = r16pUiEnabled();
  const owner = useChatsOwner();
  const [params] = useSearchParams();
  const initialSlot = slotNameFrom(params.get('slot'));
  const settingsQ = useQuery({
    queryKey: chatsKeys.settings(owner),
    queryFn: () => chatsApi.settings().then((r) => r.data),
    enabled: on,
    retry: false,
    staleTime: 60000,
  });
  const err = settingsQ.error ? classifyChatsError(settingsQ.error) : null;
  // the slot list only once the chat settings answered: a login with no chat access (403 no_chat_access) or a server without the
  // route draws no erase card, so it asks for nothing more
  const slotsQ = useQuery({ queryKey: ['slots'], queryFn: () => slotsApi.list().then((r) => r.data), enabled: on && settingsQ.isSuccess, staleTime: 300000 });
  const slotNames = useMemo(() => (Array.isArray(slotsQ.data) ? slotsQ.data.map(slotKey).filter(Boolean) : []), [slotsQ.data]);
  useEffect(() => {
    if (err) chatLog('info', 'chat data page unavailable', { kind: err.kind, status: err.status, code: err.code });
  }, [err]);

  if (!on) return <Navigate to="/dashboard" replace />;

  const header = (
    <PageHeader icon={Database} title="Chat data" back={{ to: '/inbox', label: 'Inbox' }}
      description="How long your slots' DM text is kept on NecroMancer's server, and erasing one slot's chat data." />
  );
  if (err && err.kind === 'routeMissing') {
    return (
      <div data-testid="chatdata-page" data-state="route-missing">
        {header}
        <div className="aurora-card aurora-card-dashed p-8 text-center text-sm text-muted-foreground" data-testid="chatdata-no-route">
          This server has no chat data settings yet (it needs the r16p server).
        </div>
      </div>
    );
  }
  if (err && (err.kind === 'notEnabled' || err.kind === 'sessionRequired' || err.kind === 'noAccess')) {
    return (
      <div data-testid="chatdata-page" data-state={err.kind === 'notEnabled' ? 'off' : 'no-access'}>
        {header}
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-4 text-sm text-muted-foreground" data-testid="chatdata-off">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {err.message || (err.kind === 'notEnabled' ? 'Chats are switched off on this server.' : 'This sign-in has no access to the chat pages.')}
        </p>
      </div>
    );
  }
  return (
    <div className="max-w-4xl space-y-5" data-testid="chatdata-page" data-state="ready">
      {header}
      <RetentionCard settingsQ={settingsQ} owner={owner} />
      <EraseCard slotNames={slotNames} initialSlot={initialSlot} owner={owner} />
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Inbox className="h-3.5 w-3.5" aria-hidden />To delete one conversation, open it in the <Link to="/inbox" className="font-medium text-primary hover:underline">Inbox</Link> and use its menu.
      </p>
    </div>
  );
}
